import { useMemo } from 'react'
import { useTokenUSDPriceWithAmount } from '~/hooks/useTokenPrice'
import { formatFormAmount } from '~/utils/numbers'
import { useSpotSwapPair } from '~/state/dex/hooks'
import TokenLogo from '~/components/TokenLogo'
import {
  TokenRow,
  TokenValue,
  TokenAmount,
  StyledTokenLabel,
  StyledTokenNetworkLabel,
} from './styled'
import { SwapArrowIcon } from '~/components/icons'
import PriceImpact from './PriceImpact'
import OrderRefreshing from './OrderRefreshing'
import { getNetworkLogo, getChainName } from '~/utils/chain'
import { Currency } from '~/utils/fixedPoint'

interface Props {
  inputTokenAmount: string
  outputTokenAmount: string
  loading: boolean
}

const TokenItem: React.FC<{
  token: SpotSwapToken
}> = ({ token }) => {
  const { logo, symbol, chainId } = token || {}
  const networkLogo = getNetworkLogo(chainId)
  const networkName = getChainName(chainId)
  return (
    <StyledTokenLabel gap={8} align="center">
      <TokenLogo
        large
        logo={logo}
        symbol={symbol}
        space={0}
        networkLogo={networkLogo}
        showNetworkBadge={true}
      />
      <div>
        <span>{symbol}</span>
        <StyledTokenNetworkLabel>{`on ${networkName}`}</StyledTokenNetworkLabel>
      </div>
    </StyledTokenLabel>
  )
}

const TokensChangeArea: React.FC<Props> = ({
  inputTokenAmount,
  outputTokenAmount,
  loading,
}) => {
  const [inputToken, outputToken] = useSpotSwapPair()

  const inputUSDValue = useTokenUSDPriceWithAmount(
    inputToken?.address,
    inputTokenAmount,
    false,
  )
  const outputUSDValue = useTokenUSDPriceWithAmount(
    outputToken?.address,
    outputTokenAmount,
    false,
    2,
    outputToken?.chainId,
  )

  const hasTokenPrice = useMemo(() => {
    return Number(inputUSDValue) > 0 && Number(outputUSDValue) > 0
  }, [inputUSDValue, outputUSDValue])

  const inputUSDValueLiteral = useTokenUSDPriceWithAmount(
    inputToken?.address,
    inputTokenAmount,
  )
  const outputUSDValueLiteral = useTokenUSDPriceWithAmount(
    outputToken?.address,
    outputTokenAmount,
    Currency.USD,
    2,
    outputToken?.chainId,
  )

  const inputValueLiteral = Number(inputUSDValue)
    ? `~${inputUSDValueLiteral}`
    : '~'

  const outputValueLiteral = Number(outputUSDValue)
    ? `~${outputUSDValueLiteral}`
    : '~'

  return (
    <div>
      <TokenRow>
        <TokenItem token={inputToken} />
        <div style={{ textAlign: 'right' }}>
          <TokenAmount>{inputTokenAmount}</TokenAmount>
          <TokenValue>{inputValueLiteral}</TokenValue>
        </div>
      </TokenRow>
      <SwapArrowIcon />
      <TokenRow>
        <TokenItem token={outputToken} />
        <div style={{ textAlign: 'right' }}>
          <TokenAmount>
            <OrderRefreshing
              loading={loading}
              renderText={formatFormAmount(outputTokenAmount)}
            />
          </TokenAmount>
          <TokenValue>
            <OrderRefreshing
              loading={loading}
              renderText={
                <>
                  {outputValueLiteral}
                  {hasTokenPrice && <PriceImpact />}
                </>
              }
              skeletonStyle={{ display: 'inline-block', width: 60 }}
            />
          </TokenValue>
        </div>
      </TokenRow>
    </div>
  )
}

export default TokensChangeArea
